import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import HomeIcon from '@mui/icons-material/Home';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SettingsRemoteIcon from '@mui/icons-material/SettingsRemote';
import SettingsIcon from '@mui/icons-material/Settings';
import { useSelector } from 'react-redux';
import { selectUser } from '../Redux/currentUserSlice';

// Actual rendered height of the fixed bottom nav (padding + icon/label content).
// Any page with content that could reach the bottom of the viewport should add
// this much bottom padding/margin — or drop in <FooterSpacer /> — so the fixed
// bar never overlaps the last row, button, or field on the page.
export const FOOTER_HEIGHT = 62; // px, excludes safe-area inset (added separately below)

const Main = styled.nav`
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100vw;
  background: linear-gradient(135deg, #476af7 0%, #3a5ce0 100%);
  padding: 8px 6px calc(8px + env(safe-area-inset-bottom, 0px));
  z-index: 1000;
  display: flex;
  justify-content: space-around;
  align-items: center;
  box-shadow: 0 -4px 16px rgba(35, 52, 143, 0.25);
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
`;

const StyledLink = styled(Link)`
  position: relative;
  color: rgba(255,255,255,0.85);
  text-decoration: none;
  font-size: 12px;
  font-weight: 500;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  padding: 7px 14px;
  border-radius: 12px;
  transition: background-color 0.2s ease, color 0.2s ease, transform 0.15s ease;
  -webkit-tap-highlight-color: transparent;

  & svg {
    font-size: 22px;
    transition: color 0.2s ease, transform 0.2s ease;
  }

  &:hover {
    background-color: rgba(255,255,255,0.14);
  }

  &:active {
    transform: scale(0.94);
  }

  &.active {
    background-color: #ffffff;
    color: #3a5ce0;
    font-weight: 700;
    box-shadow: 0 4px 10px rgba(0,0,0,0.12);

    & svg {
      color: #3a5ce0;
      transform: translateY(-1px);
    }
  }

  @media (max-width: 600px) {
    font-size: 10px;
    padding: 6px 10px;

    & svg {
      font-size: 20px;
    }
  }
`;

const ROLE_LINKS = {
  admin: [
    { to: '/dashboard', label: 'Home', icon: <HomeIcon /> },
    { to: '/consumption-report', label: 'Reports', icon: <AssessmentIcon /> },
    { to: '/settings', label: 'Settings', icon: <SettingsIcon /> },
  ],
  super_admin: [
    { to: '/admin-dashboard', label: 'Home', icon: <HomeIcon /> },
    { to: '/add-organization', label: 'Users', icon: <AccountCircleIcon /> },
    { to: '/command-status', label: 'Commands', icon: <SettingsRemoteIcon /> },
  ],
  // Fallback for any other role (e.g. plain "user") — add entries here as needed.
  default: [
    
  ],
};

// Drop this right before <Footer /> (or anywhere near the end of scrollable
// page content) on any page that uses the fixed bottom nav, so the last
// element on the page isn't hidden underneath it.
const Spacer = styled.div`
  height: calc(${FOOTER_HEIGHT}px + env(safe-area-inset-bottom, 0px));
  flex-shrink: 0;
`;

export const FooterSpacer = () => <Spacer aria-hidden="true" />;

const Footer = () => {
  const location = useLocation();
  const user = useSelector(selectUser);

  const links = useMemo(
    () => ROLE_LINKS[user?.role] || ROLE_LINKS.default,
    [user?.role]
  );

  if (!links.length) return null;

  return (
    <Main>
      {links.map((link) => (
        <StyledLink
          key={link.to}
          to={link.to}
          className={location.pathname.startsWith(link.to) ? 'active' : ''}
        >
          {link.icon}
          {link.label}
        </StyledLink>
      ))}
    </Main>
  );
};

export default Footer;